// Device detection utility for testing across platforms
import type { DeviceInfo } from "../types/index.ts";

/**
 * Detects device information for testing and debugging
 * @returns DeviceInfo object with platform details
 */
export const detectDeviceInfo = (): DeviceInfo => {
  const userAgent = navigator.userAgent;

  // Detect platform
  let platform = "Unknown";
  if (/iPad|iPhone|iPod/.test(userAgent)) {
    platform = "iOS";
  } else if (/Android/.test(userAgent)) {
    platform = "Android";
  } else if (/Mac/.test(userAgent)) {
    platform = "macOS";
  } else if (/Win/.test(userAgent)) {
    platform = "Windows";
  } else if (/Linux/.test(userAgent)) {
    platform = "Linux";
  }

  // Detect touch support
  const touchSupport =
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    (navigator as any).msMaxTouchPoints > 0;

  return {
    userAgent,
    screenWidth: screen.width,
    screenHeight: screen.height,
    pixelRatio: window.devicePixelRatio || 1,
    touchSupport,
    platform,
  };
};

/**
 * Get viewport dimensions (useful for responsive canvas sizing)
 */
export const getViewportDimensions = () => {
  return {
    width: window.innerWidth,
    height: window.innerHeight,
  };
};

/**
 * Detect if device is in landscape orientation
 */
export const isLandscape = (): boolean => {
  return window.innerWidth > window.innerHeight;
};

/**
 * Detect if device is an iPad (including iPad Pro)
 */
export const isIPad = (): boolean => {
  const userAgent = navigator.userAgent;

  // Direct iPad detection
  if (/iPad/.test(userAgent)) {
    return true;
  }

  // iPad Pro detection (newer models report as Mac)
  if (/Macintosh/.test(userAgent)) {
    // Check for touch support (iPad Pro with iPadOS 13+)
    return navigator.maxTouchPoints > 0;
  }

  return false;
};

/**
 * Get iPad-specific safe area information
 */
export const getSafeAreaInsets = () => {
  const getCSS = (property: string): number => {
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue(property)
      .replace("px", "");
    return parseFloat(value) || 0;
  };

  return {
    top: getCSS("--safe-area-inset-top") || 0,
    right: getCSS("--safe-area-inset-right") || 0,
    bottom: getCSS("--safe-area-inset-bottom") || 0,
    left: getCSS("--safe-area-inset-left") || 0,
  };
};

/**
 * Get viewport dimensions accounting for iPad quirks
 */
export const getIPadViewportDimensions = () => {
  const isIPadDevice = isIPad();

  if (isIPadDevice) {
    // On iPad, use visual viewport if available for more accurate dimensions
    if (window.visualViewport) {
      return {
        width: window.visualViewport.width,
        height: window.visualViewport.height,
        isVisualViewport: true,
      };
    }
  }

  return {
    width: window.innerWidth,
    height: window.innerHeight,
    isVisualViewport: false,
  };
};

/**
 * Get device pixel ratio for high-DPI displays
 */
export const getPixelRatio = (): number => {
  return window.devicePixelRatio || 1;
};

/**
 * Detect if device is primarily a touch device (for hover state prevention)
 * More robust than simple touch support detection
 */
export const isPrimaryTouchDevice = (): boolean => {
  // Check for touch support
  const hasTouch =
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    (navigator as any).msMaxTouchPoints > 0;

  if (!hasTouch) return false;

  // Check if device is mobile/tablet based on user agent
  const isMobileUA =
    /iPad|iPhone|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  // For hybrid devices like iPad with keyboard/mouse, check pointer precision
  const hasCoarsePointer = window.matchMedia("(pointer: coarse)").matches;

  // If it's a known mobile device OR has coarse pointer, treat as touch device
  return isMobileUA || hasCoarsePointer;
};
